import {
  type CylinderSurface,
  type PlacementPosition,
  type ShapedComponent,
  type ShapePoint,
  type Surface,
  type SurfacePoint,
  shapeHolesOf,
  shapeRingOf,
} from '@repo/api-contracts';
import { cylinderMmToPhoto, cylinderRectOnPhoto } from './cylinder-surface-mapping';
import { type PhotoDimensions, type PhotoPolyline, surfaceMmToPhoto } from './flat-surface-mapping';
import { surfaceRectOnPhoto } from './surface-mapping';

/** 원통 위 모양 변을 표본하는 밀도 — cylinderRectOnPhoto 와 같이 프레임 폭 W 를 64등분한 간격(mm)보다 촘촘하게. */
const SAMPLES_PER_FRAME_WIDTH = 64;

/** 닫힌 고리의 변마다 가로 간격이 maxStepMm 이하가 되게 점을 보탠다. 변마다 시작점은 늘 넣고, 끝점은 다음 변이 넣는다. */
const densifyRing = (ring: readonly ShapePoint[], maxStepMm: number): ShapePoint[] =>
  ring.flatMap((from, index) => {
    const to = ring[(index + 1) % ring.length] ?? from;
    const steps = Math.max(1, Math.ceil(Math.abs(to.xMm - from.xMm) / maxStepMm));
    return Array.from({ length: steps }, (_, step) => ({
      xMm: from.xMm + ((to.xMm - from.xMm) * step) / steps,
      yMm: from.yMm + ((to.yMm - from.yMm) * step) / steps,
    }));
  });

/** 표본 하나의 감은 프레임 x 와 사진 점. 뒷면이거나 프레임 높이 밖이면 photo 가 null 이다. */
interface CylinderSample {
  readonly wrappedX: number;
  readonly photo: SurfacePoint | null;
}

/**
 * 원통 위 닫힌 고리(배치 기준 mm)를 앞면에 보이는 조각들로 옮긴다.
 * x 를 둘레 2W 로 감아(wrappedPlacementSpans 와 같은 규칙) 앞면 [0, W]·높이 [0, H] 안의 표본만 cylinderMmToPhoto 로 옮기고,
 * 보이지 않는 표본이나 감김 이음매(감은 x 가 W 보다 크게 뛰는 곳)에서 조각을 끊는다. 점이 셋 미만인 조각은 버린다.
 * 조각은 closedPathOf 로 닫혀 잘린 자리를 곧은 현으로 잇는다 — 윤곽선 오버레이에는 그것으로 충분하다.
 */
const cylinderRingPieces = (
  surface: CylinderSurface,
  photo: PhotoDimensions,
  ring: readonly ShapePoint[],
  position: PlacementPosition,
): PhotoPolyline[] => {
  const { widthMm: W, heightMm: H } = surface.realSize;
  const circumference = 2 * W;
  const samples: CylinderSample[] = densifyRing(ring, W / SAMPLES_PER_FRAME_WIDTH).map((point) => {
    const xMm = position.xMm + point.xMm;
    const yMm = position.yMm + point.yMm;
    const wrappedX = ((xMm % circumference) + circumference) % circumference;
    const visible = wrappedX <= W && yMm >= 0 && yMm <= H;
    return {
      wrappedX,
      photo: visible ? cylinderMmToPhoto(surface, photo, { xMm: wrappedX, yMm }) : null,
    };
  });
  const isBreak = (previous: CylinderSample, next: CylinderSample): boolean =>
    previous.photo === null ||
    next.photo === null ||
    Math.abs(next.wrappedX - previous.wrappedX) > W;

  const pieces: SurfacePoint[][] = [];
  let current: SurfacePoint[] = [];
  samples.forEach((sample, index) => {
    const previous = samples[index - 1];
    if (previous !== undefined && isBreak(previous, sample) && current.length > 0) {
      pieces.push(current);
      current = [];
    }
    if (sample.photo !== null) current.push(sample.photo);
  });
  if (current.length > 0) pieces.push(current);

  // 고리는 닫혀 있다 — 마지막 표본에서 첫 표본으로 끊김 없이 이어지면 마지막 조각과 첫 조각은 한 조각이다.
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (pieces.length > 1 && first !== undefined && last !== undefined && !isBreak(last, first)) {
    const head = pieces.shift() ?? [];
    const tail = pieces.pop() ?? [];
    pieces.push([...tail, ...head]);
  }

  return pieces.filter((piece) => piece.length >= 3);
};

/**
 * 인쇄 영역 모양(shapeRingOf·shapeHolesOf)을 사진 위 닫힌 고리들로 옮긴다. 순서는 바깥 고리(조각들) → 구멍 고리들이다.
 * evenodd 로 칠하면 구멍이 비고, 같은 경로로 격자를 잘라 낼 수 있다.
 * - 사각형(shape 없음): flat 은 배치 사각형 네 모서리, cylinder 는 cylinderRectOnPhoto(기존 감김 그대로)다.
 * - 원형·다각형: flat 은 점마다 surfaceMmToPhoto, cylinder 는 cylinderRingPieces 로 앞면에 보이는 조각만 옮긴다.
 * - mesh 는 옮길 수 없어 null 이다.
 */
export const shapeRingsOnPhoto = (
  surface: Surface,
  photo: PhotoDimensions,
  component: ShapedComponent,
  position: PlacementPosition,
): readonly PhotoPolyline[] | null => {
  if (surface.type === 'mesh') return null;
  if (component.shape === undefined) {
    if (surface.type === 'cylinder')
      return cylinderRectOnPhoto(surface, photo, component, position);
    const rect = surfaceRectOnPhoto(surface, photo, component, position);
    return rect === null
      ? null
      : [
          [
            { x: rect.x, y: rect.y },
            { x: rect.x + rect.width, y: rect.y },
            { x: rect.x + rect.width, y: rect.y + rect.height },
            { x: rect.x, y: rect.y + rect.height },
          ],
        ];
  }
  const rings = [shapeRingOf(component), ...shapeHolesOf(component)];
  if (surface.type === 'cylinder') {
    return rings.flatMap((ring) => cylinderRingPieces(surface, photo, ring, position));
  }
  return rings.map((ring) =>
    ring.map((point) =>
      surfaceMmToPhoto(surface, photo, {
        xMm: position.xMm + point.xMm,
        yMm: position.yMm + point.yMm,
      }),
    ),
  );
};
